import React from "react";
import { useNavigate, NavLink } from "react-router-dom";
import "./NavBar.scss";
import { FiGrid, FiCamera, FiUser, FiSettings } from "react-icons/fi";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Cookies from "js-cookie";

const NavBar: React.FC = () => {
  const navigate = useNavigate();

  const handleCreateConsent = () => {
    navigate("/new-consentBundle");
  };

  const hostname = window.location.hostname;
  const isDevEnvironment =
    hostname === "localhost" || hostname.includes("dev.medicube");

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const logOut = () => {
    Cookies.set("token", "");
    delete axios.defaults.headers.common.Authorization;
    handleCloseUserMenu();
    window.location.replace("/");
  };

  const userQuery = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      const { data } = await axios.get("/user", {
        headers: { Accept: "application/json" },
      });

      const u =
        Array.isArray(data)
          ? typeof data[1] === "object" && data[1] !== null
            ? data[1]
            : data[0]
          : data;

      return u as any;
    },
  });

  const user = userQuery.data;

  const practiceIdRaw = user?.medicube_practice_id;
  const practiceId = practiceIdRaw != null ? Number(practiceIdRaw) : undefined;

  const getPracticeData = useQuery({
    queryKey: ["practice", practiceId],
    queryFn: async () => {
      const { data } = await axios.get(`/practice/${practiceId}`);
      return data;
    },
    enabled: !!practiceId,
  });

  const practiceName = getPracticeData.data?.name;

  const tooltipTitle =
    practiceName || (getPracticeData.isLoading ? "Loading practice..." : "My Account");

  const avatarSrc = user?.image_url || "/user-avatar.png";
  const fallbackLetter = (user?.first_name?.trim()?.[0] || "U").toUpperCase();

  return (
    <>
      {isDevEnvironment && (
        <div className="mc-env-banner">Development Sandbox Environment</div>
      )}

      <header className="mc-nav-shell">
        <div className="mc-nav">
          <div className="mc-nav__top">
            <div className="mc-nav__brand">
              <img
                className="mc-nav__logo"
                src="/logo.png"
                alt="Medicube"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>

        <nav className="mc-nav__links" aria-label="Main">
          <NavLink
            to="/welcome"
            className={({ isActive }) => `mc-nav__link ${isActive ? "is-active" : ""}`}
          >
            <FavoriteBorderIcon className="mc-nav__icon" />
            <span className="mc-nav__label">Welcome</span>
          </NavLink>

          <NavLink
            to="/dashboard"
            className={({ isActive }) => `mc-nav__link ${isActive ? "is-active" : ""}`}
          >
            <FiGrid className="mc-nav__icon" />
            <span className="mc-nav__label">Dashboard</span>
          </NavLink>

          <NavLink
            to="/video-gallery"
            className={({ isActive }) => `mc-nav__link ${isActive ? "is-active" : ""}`}
          >
            <FiCamera className="mc-nav__icon" />
            <span className="mc-nav__label">Video Gallery</span>
          </NavLink>

          <NavLink
            to="/templatesPagev2"
            className={({ isActive }) => `mc-nav__link ${isActive ? "is-active" : ""}`}
          >
            <FiUser className="mc-nav__icon" />
            <span className="mc-nav__label">My Consent Templates</span>
          </NavLink>

            <NavLink
              to="/my-plan"
              className={({ isActive }) => `mc-nav__link ${isActive ? "is-active" : ""}`}
            >
              <FiSettings className="mc-nav__icon" />
              <span className="mc-nav__label">My Plan</span>
            </NavLink>
          </nav>

            <div className="mc-nav__actions">
              <button className="mc-btn mc-btn--primary" onClick={handleCreateConsent}>
                Create consent form
              </button>

              <Tooltip title={tooltipTitle}>
                <IconButton
                  className="mc-nav__avatarBtn"
                  onClick={handleOpenUserMenu}
                  aria-controls={anchorElUser ? "user-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={anchorElUser ? "true" : undefined}
                >
                  <Avatar className="mc-nav__avatar" alt="User" src={avatarSrc}>
                    {fallbackLetter}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>

        <Menu
          id="user-menu"
          anchorEl={anchorElUser}
          open={Boolean(anchorElUser)}
          onClose={handleCloseUserMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              handleCloseUserMenu();
              navigate("/mobile_uploader");
            }}
          >
            <Typography textAlign="center">Image uploader</Typography>
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleCloseUserMenu();
              navigate("/tutorials");
            }}
          >
            <Typography textAlign="center">My Tutorials</Typography>
          </MenuItem>

          <MenuItem onClick={logOut}>
            <Typography textAlign="center">Logout</Typography>
          </MenuItem>
        </Menu>
      </header>
    </>
  );
};

export default NavBar;